import { Fragment, useEffect, useMemo, useState } from 'react';
import { useResponsive } from '@/hooks';
import {
  MenuItem,
  MenuLink,
  MenuTitle,
  MenuArrow,
  Menu,
  KeenIcon
} from '@/components';
import {
  MegaMenuSubBilling,
  MegaMenuSubDocuments,
  MegaMenuBasicSetup,
  MegaMenuSubRefferal,
  MegaMenuSubReport,
  MegaMenuMarketing,
} from '@/partials/menu/mega-menu';
import { MENU_MEGA } from '@/config';
import { hiddenModulesOf } from './hiddenModules';
import { filterMenuItems, pickTopMenuItems } from './topMenu';
import { useLanguage } from '@/i18n';
import { useAbility } from '@/providers/';
import { SUPER_ADMIN_MENU_ITEMS, isInactiveCompanyAdmin, isSuperAdmin, useAuthContext } from '@/auth';
import { Skeleton } from '@/components/ui/skeleton';
import { Can } from '@casl/react';
const MegaMenuInner = () => {
  const { ability } = useAbility();
  const { auth, loading } = useAuthContext();
  const user = auth?.user;

  const desktopMode = useResponsive('up', 'lg');
  const { isRTL } = useLanguage();

  const linkClass = 'lg:py-3.5 border-b border-b-transparent menu-item-active:border-b-gray-800 text-gray-800 menu-item-hover:text-gray-900 menu-item-active:text-gray-900 menu-item-here:border-b-gray-800 menu-item-here:text-gray-900';
  const titleClass = 'font-medium text-gray-800 text-sm';

  const superAdmin = isSuperAdmin(user);
  const [menuReady, setMenuReady] = useState(false);

  useEffect(() => {
    if (user && !loading && ability) {
      setMenuReady(true);
    }
  }, [user, loading, ability]);

  const isInactiveAdmin = isInactiveCompanyAdmin(user);

  const filteredMenuItems = useMemo(() => {
    if (!menuReady) return []

    if (superAdmin) {
      return SUPER_ADMIN_MENU_ITEMS;
    }

    // Modules the firm switched off go first, before the permission pass (OL-135).
    return filterMenuItems(MENU_MEGA, ability, hiddenModulesOf(user));
  }, [menuReady, user, ability, superAdmin]);

  if (!menuReady) {
    return (
      <div className='flex items-center py-3.5 gap-2.5'>
        {[...Array(6)].map((_, idx) => (
          <div key={idx} className='h-[20px]'>
            <Skeleton className="w-[100px] h-[16px]" />
          </div>
        ))}
      </div>
    );
  }

  if (isInactiveAdmin) {
    return null;
  }

  const buildArrow = () => (
    <MenuArrow className="flex text-gray-400 lg:hidden">
      <KeenIcon icon="plus" className="text-2xs menu-item-show:hidden" />
      <KeenIcon icon="minus" className="hidden text-2xs menu-item-show:inline-flex" />
    </MenuArrow>
  );

  const renderItems = () => {
    if (superAdmin) {
      return (
        <Fragment>
          {filteredMenuItems.map((item) => (
            <MenuItem key={item.path}>
              <MenuLink path={item.path} className={linkClass}>
                <MenuTitle className={titleClass}>{item.title}</MenuTitle>
              </MenuLink>
            </MenuItem>
          ))}
        </Fragment>
      );
    }

    const {
      home: homeItem,
      contacts: contactsItem,
      matters: matterItem,
      tasks: tasksItem,
      billing: billingItem,
      basicSetup: basicSetupItem,
      referrals: referralItem,
      reports: reportItem,
      marketing: marketingItem,
      documents: documentItem,
      sharedStorage: sharedStorageItem,
    } = pickTopMenuItems(filteredMenuItems);
    return (
      <Fragment>
        <MenuItem key="home">
          <MenuLink path={homeItem.path} className={linkClass}>
            <MenuTitle className={titleClass}>{homeItem.title}</MenuTitle>
          </MenuLink>
        </MenuItem>

        {/* Contacts Items */}
        {contactsItem?.title && (
          <Can I={contactsItem.permission?.action} a={contactsItem.permission?.subject} ability={ability}>
            <MenuItem key="contactsItem">
              <MenuLink path={contactsItem.path} className={linkClass}>
                <MenuTitle className={titleClass}>{contactsItem.title}</MenuTitle>
              </MenuLink>
            </MenuItem>
          </Can>
        )}

        {/* Matters Items */}
        {matterItem?.title && (
          <Can I={matterItem.permission?.action} a={matterItem.permission?.subject} ability={ability}>
            <MenuItem key="matterItem">
              <MenuLink path={matterItem.path} className={linkClass}>
                <MenuTitle className={titleClass}>{matterItem.title}</MenuTitle>
              </MenuLink>
            </MenuItem>
          </Can>
        )}

        {/* Tasks (OL-210) */}
        {tasksItem?.title && (
          <Can I={tasksItem.permission?.action} a={tasksItem.permission?.subject} ability={ability}>
            <MenuItem key="tasksItem">
              <MenuLink path={tasksItem.path} className={linkClass}>
                <MenuTitle className={titleClass}>{tasksItem.title}</MenuTitle>
              </MenuLink>
            </MenuItem>
          </Can>
        )}

        {/* Secure File Sharing */}
        {sharedStorageItem?.title && (
          <Can
            I={sharedStorageItem.permission?.action}
            a={sharedStorageItem.permission?.subject}
            ability={ability}
          >
            <MenuItem key="sharedStorageItem">
              <MenuLink path={sharedStorageItem.path} className={linkClass}>
                <MenuTitle className={titleClass}>{sharedStorageItem.title}</MenuTitle>
              </MenuLink>
            </MenuItem>
          </Can>
        )}

        {/* Documents Items */}
        {documentItem?.children?.length > 0 && (
          <MenuItem key="documents" toggle={desktopMode ? 'dropdown' : 'accordion'} trigger={desktopMode ? 'hover' : 'click'} dropdownProps={{
            placement: isRTL() ? 'bottom-end' : 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: isRTL() ? [20, 0] : [-20, 0] } }]
          }}>
            <MenuLink className={linkClass}>
              <MenuTitle className={titleClass}>{documentItem.title}</MenuTitle>
              {buildArrow()}
            </MenuLink>
            {MegaMenuSubDocuments(filteredMenuItems)}
          </MenuItem>
        )}

        {/* Billing Items */}
        {billingItem?.children?.length > 0 && (
          <MenuItem key="billing" toggle={desktopMode ? 'dropdown' : 'accordion'} trigger={desktopMode ? 'hover' : 'click'} dropdownProps={{
            placement: isRTL() ? 'bottom-end' : 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: isRTL() ? [20, 0] : [-20, 0] } }]
          }}>
            <MenuLink className={linkClass}>
              <MenuTitle className={titleClass}>{billingItem.title}</MenuTitle>
              {buildArrow()}
            </MenuLink>
            {MegaMenuSubBilling(filteredMenuItems)}
          </MenuItem>
        )}

        {/* Referrals Items */}
        {referralItem?.children?.length > 0 && (
          <MenuItem key="referral" toggle={desktopMode ? 'dropdown' : 'accordion'} trigger={desktopMode ? 'hover' : 'click'} dropdownProps={{
            placement: isRTL() ? 'bottom-end' : 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: isRTL() ? [20, 0] : [-20, 0] } }]
          }}>
            <MenuLink className={linkClass}>
              <MenuTitle className={titleClass}>{referralItem.title}</MenuTitle>
              {buildArrow()}
            </MenuLink>
            {MegaMenuSubRefferal(filteredMenuItems)}
          </MenuItem>
        )}

        {/* Reports Items */}
        {reportItem?.children?.length > 0 && (
          <MenuItem key="report" toggle={desktopMode ? 'dropdown' : 'accordion'} trigger={desktopMode ? 'hover' : 'click'} dropdownProps={{
            placement: isRTL() ? 'bottom-end' : 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: isRTL() ? [20, 0] : [-20, 0] } }]
          }}>
            <MenuLink className={linkClass}>
              <MenuTitle className={titleClass}>{reportItem.title}</MenuTitle>
              {buildArrow()}
            </MenuLink>
            {MegaMenuSubReport(filteredMenuItems)}
          </MenuItem>
        )}

        {marketingItem?.children?.length > 0 && (
          <MenuItem key="marketing" toggle={desktopMode ? 'dropdown' : 'accordion'} trigger={desktopMode ? 'hover' : 'click'} dropdownProps={{
            placement: isRTL() ? 'bottom-end' : 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: isRTL() ? [20, 0] : [-20, 0] } }]
          }}>
            <MenuLink className={linkClass}>
              <MenuTitle className={titleClass}>{marketingItem.title}</MenuTitle>
              {buildArrow()}
            </MenuLink>
            {MegaMenuMarketing(filteredMenuItems)}
          </MenuItem>
        )}

        {basicSetupItem?.children?.length > 0 && (
          <MenuItem key="basic-setup" toggle={desktopMode ? 'dropdown' : 'accordion'} trigger={desktopMode ? 'hover' : 'click'} dropdownProps={{
            placement: isRTL() ? 'bottom-end' : 'bottom-start',
            modifiers: [{ name: 'offset', options: { offset: isRTL() ? [20, 0] : [-20, 0] } }]
          }}>
            <MenuLink className={linkClass}>
              <MenuTitle className={titleClass}>{basicSetupItem.title}</MenuTitle>
              {buildArrow()}
            </MenuLink>
            {MegaMenuBasicSetup(filteredMenuItems)}
          </MenuItem>
        )}
      </Fragment>
    );
  };

  return (
    <Menu multipleExpand highlight className="flex-col lg:flex-row gap-5 lg:gap-7.5 p-5 lg:p-0">
      {renderItems()}
    </Menu>
  );
};

export { MegaMenuInner };
